/**
 * BhiSystemicFactorsCard — iter1114.b
 *
 * Educational card on the BHI dashboard that pulls three systemic
 * factors (homocysteine from Cardio twin, DHEA-S / adrenal + Estriol
 * from Hormonal twin) and shows them as compact factor cards so the
 * breast-health picture includes methylation + adrenal context.
 *
 * These three factors are legitimate BHI modifiers:
 *   • Homocysteine ↑ → methylation deficit → impaired estrogen 2-OH
 *     detox pathway → BHI risk elevator
 *   • DHEA-S low   → chronic stress / adrenal fatigue → cortisol-estrogen
 *     imbalance → BHI risk elevator
 *   • Estriol      → protective (E3) estrogen; low = E2 dominance
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Heart, Brain, Flower2, Info } from "lucide-react";

const ROLE_META = {
  homocysteine: {
    icon: Heart,
    group_bg: "Метилиране (от Кардио близнак)",
    why_bg:
      "Високият хомоцистеин показва недостиг на метилиране — забавя защитния 2-OH-E2 път на разграждане на естрогена, важен за здравето на гърдата.",
  },
  adrenal: {
    icon: Brain,
    group_bg: "Надбъбречна функция (от Хормонален близнак)",
    why_bg:
      "DHEA-S е показател за надбъбречния резерв. Продължителното натоварване и ниското DHEA-S нарушават равновесието между кортизол и естроген.",
  },
  estriol: {
    icon: Flower2,
    group_bg: "Защитен естроген (от Хормонален близнак)",
    why_bg:
      "Естриолът (E3) е най-леката форма на естрогена и действа като буфер срещу E2-доминантен метаболизъм.",
  },
};

const BAND_COLOR = {
  green:  "#10B981",
  yellow: "#F59E0B",
  orange: "#EA580C",
  red:    "#EF4444",
};

export default function BhiSystemicFactorsCard({ isDark = false }) {
  const { API } = useAuth();
  const [cardio, setCardio] = useState(null);
  const [hormonal, setHormonal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/cardio-twin/snapshot`),
      axios.get(`${API}/hormonal-twin/snapshot`),
    ])
      .then(([c, h]) => {
        if (c.status === "fulfilled") setCardio(c.value.data || {});
        if (h.status === "fulfilled") setHormonal(h.value.data || {});
      })
      .finally(() => setLoading(false));
  }, [API]);

  const factors = useMemo(() => {
    const list = [];
    const hcy = cardio?.homocysteine_factor;
    if (hcy && hcy.value) list.push({ role: "homocysteine", ...hcy });
    const adr = hormonal?.adrenal_factor;
    if (adr && adr.value) list.push({ role: "adrenal", ...adr });
    const e3  = hormonal?.estriol_factor;
    if (e3 && e3.value) list.push({ role: "estriol", ...e3 });
    return list;
  }, [cardio, hormonal]);

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800"
    : "bg-white border-slate-200";

  if (loading) return null;
  if (factors.length === 0) {
    // Educational empty-state so users know what unlocks this card.
    return (
      <div className={`p-4 rounded-xl border ${cardCls}`} data-testid="bhi-systemic-factors-empty">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 opacity-70 shrink-0" />
          <div className="text-xs">
            <div className="font-semibold mb-0.5">Системни модификатори</div>
            <p className="opacity-70 leading-snug">
              Качете <strong>хомоцистеин</strong>, <strong>DHEA-S</strong> или{" "}
              <strong>естриол</strong> в раздел „Лабораторни изследвания", за да
              видите как метилирането и надбъбречната функция влияят на здравето
              на гърдата.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-4 rounded-xl border ${cardCls}`}
      data-testid="bhi-systemic-factors-card"
    >
      <div className="mb-3">
        <h3 className="text-sm font-bold">
          Системни модификатори за здравето на гърдата
        </h3>
        <p className="text-[11px] opacity-70 leading-snug mt-0.5">
          Контекст от Кардио и Хормонален близнак — метилиране, надбъбречна
          функция, защитен естроген. {factors.length} от 3 налични.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {factors.map((f) => {
          const meta = ROLE_META[f.role];
          const Icon = meta.icon;
          const color = BAND_COLOR[f.band] || "#64748B";
          return (
            <div
              key={f.role}
              className={`relative p-3 rounded-lg border overflow-hidden ${
                isDark ? "bg-slate-950/50 border-slate-700" : "bg-slate-50 border-slate-200"
              }`}
              data-testid={`bhi-systemic-factor-${f.role}`}
            >
              {/* Left colored strip */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ backgroundColor: color }}
              />
              <div className="pl-2">
                <div className="flex items-start gap-1.5">
                  <Icon
                    className="w-3.5 h-3.5 mt-0.5 shrink-0"
                    style={{ color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase opacity-60 font-semibold tracking-wide">
                      {meta.group_bg}
                    </div>
                    <div className="font-bold text-xs leading-tight mt-0.5">
                      {f.label_bg}
                    </div>
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-lg font-bold tabular-nums" style={{ color }}>
                    {f.value}
                  </span>
                  <span className="text-[10px] opacity-60">{f.unit}</span>
                  <span
                    className="ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
                    style={{ background: `${color}22`, color }}
                  >
                    {f.status}
                  </span>
                </div>
                {f.reference_bg && (
                  <div className="text-[10px] opacity-60 mt-1">
                    Референтни стойности: {f.reference_bg}
                  </div>
                )}
                {f.note_bg && (
                  <p className="text-[10px] opacity-80 leading-snug mt-1.5">
                    {f.note_bg}
                  </p>
                )}
                <div className="text-[9px] opacity-60 italic mt-1.5 border-t pt-1" style={{ borderColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }}>
                  Защо за здравето на гърдата: {meta.why_bg}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
